/**
 * One role's access, as a single editable draft.
 *
 * Configuring a role used to mean three screens — actions, modules, menus —
 * each with its own role picker, its own load and its own Save button. Nothing
 * told you what the role could do once you had finished, and it was entirely
 * possible to save two of the three and walk away.
 *
 * So the draft is one object, and saving it is one action that writes only the
 * parts that actually changed. Kept free of React so the arithmetic that
 * decides "changed" can be tested on its own — a false negative here silently
 * discards an operator's edit.
 */

/** Order-insensitive, duplicate-insensitive: the server stores these sorted. */
const sameSet = (a, b) => {
  if (a === null || b === null) return a === b;
  const left = [...new Set(a || [])].sort();
  const right = [...new Set(b || [])].sort();
  return left.length === right.length && left.every((v, i) => v === right[i]);
};

/**
 * `null` means "no restriction" for modules and menus, and it is not the same
 * as `[]`. Reading one as the other either blanks a role's access or silently
 * lifts a restriction, so the two are kept distinct all the way through.
 */
export const buildDraft = ({ permissions = [], modules = null, menus = null } = {}) => ({
  permissions: [...permissions],
  modules: modules === null ? null : [...modules],
  menus: menus === null ? null : [...menus],
});

export const diffRoleAccess = (original, draft) => ({
  permissions: !sameSet(original.permissions, draft.permissions),
  modules: !sameSet(original.modules, draft.modules),
  menus: !sameSet(original.menus, draft.menus),
});

export const hasChanges = (original, draft) =>
  Object.values(diffRoleAccess(original, draft)).some(Boolean);

/**
 * What the operator is about to do, in words, before they do it.
 *
 * A restriction being lifted or imposed is called out rather than counted:
 * going from "every module" to a list of nineteen is not "−13 modules", it is a
 * different kind of change and reads as one.
 */
export const describeChanges = (original, draft) => {
  const lines = [];
  const countChange = (label, before, after) => {
    const added = after.filter((v) => !before.includes(v)).length;
    const removed = before.filter((v) => !after.includes(v)).length;
    if (added) lines.push(`+${added} ${label}`);
    if (removed) lines.push(`−${removed} ${label}`);
  };

  if (!sameSet(original.permissions, draft.permissions)) {
    countChange('actions', original.permissions || [], draft.permissions || []);
  }
  [['modules', 'modules'], ['menus', 'menus']].forEach(([key, label]) => {
    if (sameSet(original[key], draft[key])) return;
    if (original[key] === null) lines.push(`${label} restricted to ${draft[key].length}`);
    else if (draft[key] === null) lines.push(`${label} restriction removed`);
    else countChange(label, original[key], draft[key]);
  });
  return lines;
};

/**
 * The requests to send, and only those. Saving all three every time would
 * rewrite rows the operator never touched — which matters because a write here
 * stamps the row as restricted even when the list is unchanged.
 */
export const savePlan = (original, draft) => {
  const changed = diffRoleAccess(original, draft);
  const plan = [];
  if (changed.permissions) {
    plan.push({ kind: 'permissions', body: { permissions: draft.permissions, disabled_permissions: [] } });
  }
  if (changed.modules) plan.push({ kind: 'modules', body: { modules: draft.modules } });
  if (changed.menus) plan.push({ kind: 'menus', body: { menus: draft.menus } });
  return plan;
};
